import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { apiRequest, getAuthToken } from '../utils/api';

const SYMBOLS = ['SPY', 'QQQ', 'SPX'];
const POLL_MS = 1000;

// SPX index only publishes values during its session window (~8am–5pm ET);
// hide it outside that range so a frozen overnight value isn't misleading.
const SPX_WINDOW_START_MIN = 8 * 60;
const SPX_WINDOW_END_MIN = 17 * 60;
const isSpxWindow = () => {
  const et = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const mins = et.getHours() * 60 + et.getMinutes();
  return mins >= SPX_WINDOW_START_MIN && mins < SPX_WINDOW_END_MIN;
};

const fmt = (n, digits = 2) =>
  n == null || !Number.isFinite(Number(n)) ? '—' : Number(n).toFixed(digits);

const fmtSigned = (n, digits = 2) => {
  if (n == null || !Number.isFinite(Number(n))) return '';
  const v = Number(n);
  return `${v >= 0 ? '+' : ''}${v.toFixed(digits)}`;
};

/**
 * Live index ticker for the top app bar — polls /api/market-quotes once per
 * second. The backend resolves quotes via the owner's broker credentials
 * (Schwab first) whose feed already reflects extended-hours trades, so the
 * change shown is always versus the prior regular close.
 */
export default function MarketTicker() {
  const [quotes, setQuotes] = useState({});

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const tick = async () => {
      if (!getAuthToken() || document.hidden) return;
      try {
        const res = await apiRequest(`/api/market-quotes?symbols=${SYMBOLS.join(',')}`);
        const data = res && res.data;
        // Only replace state when the response actually carries quotes — an
        // empty/hiccup poll must not blank the bar.
        if (!cancelled && data && data.quotes && Object.keys(data.quotes).length) {
          setQuotes(prev => ({ ...prev, ...data.quotes }));
        }
      } catch {
        // transient quote failure — keep last displayed values
      }
    };

    const onVisibility = () => { if (!document.hidden) tick(); };
    tick();
    timer = setInterval(tick, POLL_MS);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  if (!Object.keys(quotes).length) return null;

  const spxVisible = isSpxWindow();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
      {SYMBOLS.map(sym => {
        if (sym === 'SPX' && !spxVisible) return null;
        const q = quotes[sym];
        if (!q || q.last == null) return null;
        // Display mid (drifts overnight via quotes) when bid/ask exist —
        // `last` only moves on real prints, so it sits frozen after hours.
        const px = (q.bid > 0 && q.ask > 0) ? (q.bid + q.ask) / 2 : q.last;
        const chg = q.close ? px - q.close : q.change;
        const pct = q.close ? (chg / q.close) * 100 : q.changePct;
        const up = chg == null ? null : chg >= 0;
        const color = up == null ? 'rgba(255,255,255,0.9)' : up ? '#7ee787' : '#ff9e9e';
        return (
          <Box key={sym} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, whiteSpace: 'nowrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>
              {sym}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff', fontSize: '0.95rem', fontVariantNumeric: 'tabular-nums' }}>
              {fmt(px)}
            </Typography>
            {chg != null && (
              <Typography variant="body2" sx={{ fontWeight: 600, color, fontSize: '0.9rem', fontVariantNumeric: 'tabular-nums' }}>
                {fmtSigned(chg)}{pct != null ? ` (${fmtSigned(pct)}%)` : ''}
              </Typography>
            )}
            {q.quoteTs && (
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.7rem', fontVariantNumeric: 'tabular-nums' }}>
                {new Date(q.quoteTs).toLocaleTimeString()}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
